(() => {
  function initCategoryCarousel(carousel) {
    if (!carousel || carousel.dataset.carouselReady) return;
    const track = carousel.querySelector('[data-category-track]');
    const controls = carousel.querySelector('[data-category-controls]');
    const toggle = carousel.querySelector('[data-category-toggle]');
    if (!track || !controls || !toggle || track.children.length < 2) return;
    carousel.dataset.carouselReady = 'true';
    controls.hidden = false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let playing = carousel.dataset.autoplay === 'true' && !reducedMotion.matches && !window.Shopify?.designMode;
    let hovered = false;
    let focused = false;
    let visible = !('IntersectionObserver' in window);
    let moving = false;
    let timer;
    let settle;
    const interval = Math.max(3000, Number(carousel.dataset.interval) || 3000);
    const syncToggle = () => {
      const label = reducedMotion.matches ? carousel.dataset.reducedLabel : playing ? carousel.dataset.pauseLabel : carousel.dataset.playLabel;
      toggle.disabled = reducedMotion.matches;
      toggle.setAttribute('aria-pressed', String(playing));
      toggle.setAttribute('aria-label', label);
      toggle.title = label;
      toggle.querySelector('[data-category-pause]').hidden = !playing;
      toggle.querySelector('[data-category-play]').hidden = playing;
      track.setAttribute('aria-live', playing ? 'off' : 'polite');
    };
    function schedule(delay = interval) {
      clearTimeout(timer);
      if (!carousel.isConnected || !playing || hovered || focused || !visible || document.hidden || moving) return;
      timer = setTimeout(() => move(1, false), delay);
    }
    function pause() {
      playing = false;
      syncToggle();
      schedule();
    }
    function move(direction, manual = true) {
      if (manual) pause();
      if (moving || track.scrollWidth <= track.clientWidth + 2) return;
      const cards = Array.from(track.children);
      const step = cards[1].offsetLeft - cards[0].offsetLeft;
      if (step <= 0) return;
      moving = true;
      const startedAt = performance.now();
      clearTimeout(timer);
      track.style.scrollSnapType = 'none';
      let left = track.scrollLeft;
      if (direction > 0 && left + step > track.scrollWidth - track.clientWidth + 2) {
        while (left >= step - 2) {
          track.appendChild(track.firstElementChild);
          left = Math.max(0, left - step);
        }
        track.scrollTo({ left, behavior: 'instant' });
      } else if (direction < 0 && left < step - 2) {
        track.prepend(track.lastElementChild);
        left += step;
        track.scrollTo({ left, behavior: 'instant' });
      }
      requestAnimationFrame(() => {
        track.scrollTo({ left: left + direction * step, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
        settle = setTimeout(() => {
          track.style.scrollSnapType = '';
          moving = false;
          schedule(Math.max(0, interval - (performance.now() - startedAt)));
        }, reducedMotion.matches ? 50 : 700);
      });
    }
    carousel.querySelector('[data-category-previous]').addEventListener('click', () => move(-1));
    carousel.querySelector('[data-category-next]').addEventListener('click', () => move(1));
    toggle.addEventListener('click', () => {
      if (reducedMotion.matches) return;
      playing = !playing;
      syncToggle();
      schedule();
    });
    carousel.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'mouse') return;
      hovered = true;
      schedule();
    });
    carousel.addEventListener('pointerleave', () => { hovered = false; schedule(); });
    carousel.addEventListener('focusin', () => { focused = true; schedule(); });
    carousel.addEventListener('focusout', event => {
      focused = carousel.contains(event.relatedTarget);
      schedule();
    });
    track.addEventListener('pointerdown', pause, { passive: true });
    track.addEventListener('wheel', pause, { passive: true });
    track.addEventListener('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      move(event.key === 'ArrowRight' ? 1 : -1);
    });
    reducedMotion.addEventListener('change', () => { if (reducedMotion.matches) pause(); syncToggle(); });
    const visibilityChanged = () => schedule();
    document.addEventListener('visibilitychange', visibilityChanged);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      schedule();
    }, { threshold: 0.15 }) : null;
    observer?.observe(carousel);
    document.addEventListener('shopify:block:select', event => {
      if (!carousel.contains(event.target)) return;
      pause();
      event.target.closest('.tcp-h-category')?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    });
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(carousel)) return;
      clearTimeout(timer);
      clearTimeout(settle);
      observer?.disconnect();
      document.removeEventListener('visibilitychange', visibilityChanged);
    });
    syncToggle();
    schedule();
  }
  function initPromotion(root, strings) {
    const card = root.querySelector('[data-home-promotion-expires]');
    const template = root.querySelector('[data-home-promotion-fallback]');
    if (!card || !template) return;
    const expires = Number(card.dataset.homePromotionExpires) * 1000;
    let timer;
    let ended = false;
    function check() {
      clearTimeout(timer);
      if (ended || !root.isConnected) return;
      const remaining = expires - Date.now();
      if (!Number.isFinite(remaining) || remaining <= 0) {
        ended = true;
        const focused = card.contains(document.activeElement);
        const replacement = template.content.cloneNode(true);
        const next = replacement.querySelector('[data-home-card]');
        card.replaceWith(replacement);
        template.remove();
        const status = root.querySelector('[data-home-promotion-status]');
        if (status) status.textContent = strings.campaignEnded;
        if (focused) next?.querySelector('a')?.focus({preventScroll: true});
        document.removeEventListener('visibilitychange', check);
        return;
      }
      timer = setTimeout(check, Math.min(remaining + 30, 2147483647));
    }
    document.addEventListener('visibilitychange', check);
    document.addEventListener('shopify:section:unload', event => {
      if (!event.target.contains(root)) return;
      ended = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', check);
    });
    check();
  }
  function initFooter(footer) {
    if (footer.dataset.footerReady) return;
    footer.dataset.footerReady = 'true';
    const details = footer.querySelector('[data-home-footer-policies]');
    if (!details) return;
    const media = window.matchMedia('(min-width: 750px)');
    const update = () => { details.open = media.matches; };
    media.addEventListener('change', update);
    update();
  }
  function init(root) {
    if (!root || root.dataset.homeReady) return;
    root.dataset.homeReady = 'true';
    const stringsNode = root.querySelector('[data-home-strings]');
    if (!stringsNode) return;
    let strings;
    try { strings = JSON.parse(stringsNode.textContent); } catch (_) { return; }
    initCategoryCarousel(root.querySelector('[data-category-carousel]'));
    initPromotion(root, strings);
    const bar = root.querySelector('[data-home-cart]');
    const minimum = Number(root.dataset.minimum);
    const freeFrom = Number(root.dataset.freeFrom);
    const pathRoot = window.Shopify?.routes?.root || root.dataset.root || '/';
    const routeRoot = pathRoot.endsWith('/') ? pathRoot : pathRoot + '/';
    let currency = root.dataset.currency || 'AUD';
    let busy = false;
    const money = amount => new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(amount / 100);
    const text = (value, replacements = {}) => Object.entries(replacements).reduce((result, [key, replacement]) => result.split('__' + key + '__').join(String(replacement)), value);
    root.classList.add('is-enhanced');
    const more = root.querySelector('[data-home-more]');
    if (more && root.querySelectorAll('[data-home-card]').length > 2) {
      more.hidden = false;
      more.addEventListener('click', () => {
        const expanded = root.classList.toggle('is-expanded');
        more.setAttribute('aria-expanded', String(expanded));
        more.querySelector('[data-home-more-label]').textContent = expanded ? strings.fewer : strings.more;
        if (!expanded && window.matchMedia('(max-width: 749px)').matches) more.scrollIntoView({block: 'nearest', behavior: 'auto'});
      });
    }
    root.addEventListener('click', event => {
      const quantityButton = event.target.closest('[data-home-quantity]');
      if (quantityButton && root.contains(quantityButton)) {
        const form = quantityButton.closest('form');
        const input = form?.querySelector('input[name="quantity"]');
        if (!input || busy) return;
        const step = Number(input.step) || 1;
        const min = Number(input.min) || 1;
        const max = input.max ? Number(input.max) : Infinity;
        const current = Number(input.value) || min;
        input.value = String(Math.max(min, Math.min(max, current + Number(quantityButton.dataset.homeQuantity) * step)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const deliveryLink = event.target.closest('[data-home-delivery]');
      if (deliveryLink && root.contains(deliveryLink)) {
        const target = root.querySelector('.tcp-h-checker');
        if (target) { event.preventDefault(); target.open = true; target.scrollIntoView({ block: 'start', behavior: 'auto' }); target.querySelector('input')?.focus({ preventScroll: true }); }
      }
    });
    root.addEventListener('change', event => {
      const select = event.target.closest('[data-home-variant]');
      if (!select) return;
      const option = select.selectedOptions[0];
      const card = select.closest('[data-home-card]');
      if (!option || !card) return;
      const price = Number(option.dataset.price);
      const compare = Number(option.dataset.compare);
      card.querySelector('[data-card-price]').textContent = money(price);
      const previous = card.querySelector('[data-compare-price]');
      previous.hidden = !(compare > price);
      previous.textContent = money(compare);
      const badge = card.querySelector('[data-savings-badge]');
      if (badge) { badge.hidden = !(compare > price); badge.textContent = text(strings.save, { amount: money(compare - price) }); }
      const unit = card.querySelector('[data-unit-price]');
      const divisor = Number(option.dataset.unitDivisor || card.dataset.unitDivisor);
      card.dataset.unitDivisor = String(divisor);
      const pack = card.querySelector('[data-card-pack]');
      if (pack && option.dataset.packCopy) pack.textContent = option.dataset.packCopy;
      if (unit && divisor > 0) unit.textContent = text(unit.dataset.unitTemplate, { amount: money(Math.round(price / divisor)) });
      card.querySelectorAll('[data-product-link]').forEach(link => { const url = new URL(link.href, location.href); url.searchParams.set('variant', select.value); link.href = url.href; });
      const available = option.dataset.available === 'true';
      const submit = card.querySelector('button[type="submit"]');
      submit.disabled = !available;
      card.querySelector('[data-home-add-label]').textContent = available ? strings.add : strings.soldOut;
      const quantity = card.querySelector('input[name="quantity"]');
      quantity.min = option.dataset.minimum || '1';
      quantity.step = option.dataset.increment || '1';
      if (option.dataset.maximum) quantity.max = option.dataset.maximum;
      else quantity.removeAttribute('max');
      quantity.value = quantity.min;
    });
    function showCart(cart, message = '') {
      if (!bar || !Number.isInteger(cart.item_count) || !Number.isFinite(cart.total_price)) return;
      currency = cart.currency || currency;
      bar.hidden = cart.item_count === 0;
      bar.querySelector('[data-home-cart-total]').hidden = false;
      bar.querySelector('[data-home-cart-total]').textContent = money(cart.total_price);
      bar.querySelector('[data-home-cart-status]').textContent = cart.item_count === 1 ? strings.itemsOne : text(strings.items, { count: cart.item_count });
      const remainingMinimum = Math.max(0, minimum - cart.total_price);
      const remainingFree = Math.max(0, freeFrom - cart.total_price);
      let progressText = strings.threshold;
      if (remainingMinimum > 0) progressText = text(strings.minimum, { amount: money(remainingMinimum), free: money(remainingFree) });
      else if (remainingFree > 0) progressText = text(strings.free, { amount: money(remainingFree) });
      bar.querySelector('[data-home-progress-text]').textContent = progressText;
      const track = bar.querySelector('[role="progressbar"]');
      track.setAttribute('aria-valuenow', String(Math.min(cart.total_price, freeFrom)));
      track.setAttribute('aria-valuetext', progressText);
      bar.querySelector('[data-home-progress-fill]').style.width = Math.min(100, cart.total_price / freeFrom * 100) + '%';
      document.querySelectorAll('.tc-header__cart').forEach(link => {
        const badge = link.querySelector('.tc-header__count');
        if (badge) { badge.textContent = String(cart.item_count); badge.hidden = cart.item_count === 0; }
        link.setAttribute('aria-label', strings.cart + ', ' + cart.item_count);
      });
    }
    async function refreshCart(message = '') {
      const response = await fetch(routeRoot + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('cart-read');
      const cart = await response.json();
      showCart(cart, message);
      document.dispatchEvent(new CustomEvent('cart:items-updated', { detail: { cart } }));
      return cart;
    }
    root.addEventListener('submit', async event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('.tcp-h-card__form')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy || !form.reportValidity()) return;
      busy = true;
      const card = form.closest('[data-home-card]');
      const feedback = form.querySelector('[data-home-feedback]');
      const label = form.querySelector('[data-home-add-label]');
      const formData = new FormData(form);
      const quantity = Number(formData.get('quantity'));
      if (!Number.isInteger(quantity) || quantity <= 0) { busy = false; return; }
      const controls = Array.from(root.querySelectorAll('.tcp-h-card__form button, .tcp-h-card__form input:not([type="hidden"]), .tcp-h-card__form select'), node => [node, node.disabled]);
      controls.forEach(([node]) => { node.disabled = true; });
      feedback.textContent = '';
      delete feedback.dataset.error;
      form.setAttribute('aria-busy', 'true');
      label.textContent = strings.adding;
      let confirmed = false;
      try {
        const response = await fetch(routeRoot + 'cart/add.js', { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: formData });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : strings.error);
        confirmed = true;
        const added = text(strings.added, { quantity, product: card.dataset.productTitle });
        feedback.textContent = added;
        await refreshCart(added);
      } catch (error) {
        feedback.dataset.error = 'true';
        feedback.textContent = confirmed ? strings.error : error.message === 'Failed to fetch' ? strings.error : error.message || strings.error;
        if (bar) { bar.hidden = false; bar.querySelector('[data-home-cart-status]').textContent = strings.error; }
        try { await refreshCart(); } catch (_) { if (bar) bar.querySelector('[data-home-cart-total]').hidden = true; }
      } finally {
        form.removeAttribute('aria-busy');
        label.textContent = strings.add;
        controls.forEach(([node, disabled]) => { node.disabled = disabled; });
        busy = false;
      }
    }, true);
    if (bar) {
      refreshCart().catch(() => {});
      window.addEventListener('pageshow', () => { refreshCart().catch(() => {}); });
      document.addEventListener('cart:items-updated', event => { if (root.isConnected && event.detail?.cart) showCart(event.detail.cart); });
    }
  }
  function start(container = document) { container.querySelectorAll('[data-category-home]').forEach(init); container.querySelectorAll('.tcp-h-footer').forEach(initFooter); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start(), { once: true });
  else start();
  document.addEventListener('shopify:section:load', event => start(event.target));
})();


